import ThemedElement from "shared/components/ThemedElement";
import React, { useState, useEffect, useRef } from "react";
import {
  CircularProgress,
  Alert,
  InputAdornment,
  Card,
  Tooltip,
  Snackbar,
  IconButton,
  Box,
  Grid,
  Chip,
  Paper,
  TextField,
  alpha,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import {
  Search,
  Refresh,
  Receipt,
  Delete,
  Edit,
  FilterAlt,
  Clear,
  Download,
  CheckCircle,
  Schedule,
  PictureAsPdf,
  CreditCard,
  ContentCopy,
  Link as LinkIcon,
  InfoOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import {
  getFinancialDataService,
  deleteInvoiceService,
} from "modules/financiera/services/facturaService";
import ModalEditarFactura from "../Form/ModalEditarFactura";
import ModalConfirmarEliminar from "../Form/ModalConfirmarEliminar";
import PaymentReceiptModal from "./PaymentReceiptModal";
import { facturaActionSx, facturaTableSx } from "../../facturasStyles";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}/${month}/${year}`;
};

const getColombiaDateString = (date) => {
  if (!date) return null;
  const d = new Date(date);
  const colombiaDate = new Date(d.getTime() - 5 * 60 * 60 * 1000);
  return colombiaDate.toISOString().split("T")[0];
};

const isDateInRange = (dateString, startDate, endDate) => {
  if (!dateString) return false;
  const itemDateStr = getColombiaDateString(dateString);
  if (startDate && endDate) return itemDateStr >= startDate && itemDateStr <= endDate;
  if (startDate) return itemDateStr >= startDate;
  if (endDate) return itemDateStr <= endDate;
  return true;
};

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  success: "#27ae60",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#2980b9",
  white: "#ffffff",
  gray: "#f5f5f5",
  darkGray: "#333333",
  border: "#e2e8f0",
};

function TableFacturasContent({ refreshFlag, onError }) {
  const [facturasData, setFacturasData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [typeFilter, setTypeFilter] = useState("todos");
  const [tableKey, setTableKey] = useState(0);

  const [selectedFactura, setSelectedFactura] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [facturaToDelete, setFacturaToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedInvoiceForPdf, setSelectedInvoiceForPdf] = useState(null);

  const fetchFacturas = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getFinancialDataService();

      if (result.success) {
        const data = result.data || [];
        const processedData = data.map((item) => ({
          ...item,
          createdAtDateOnly: getColombiaDateString(item.createdAt),
          createdAtFormatted: item.createdAt
            ? (() => {
                const colombiaDate = new Date(
                  new Date(item.createdAt).getTime() - 5 * 60 * 60 * 1000
                );
                return colombiaDate.toLocaleDateString("es-CO", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              })()
            : "N/A",
        }));
        const sortedData = [...processedData].sort((a, b) => b.id - a.id);
        setFacturasData(sortedData);
        setFilteredData(sortedData);
        if (onError) onError(null);
      } else {
        const errorMsg = result.error || "Error al cargar datos";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Error al cargar facturas";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const refreshDataPreservePage = async () => {
    setLoading(true);
    try {
      const result = await getFinancialDataService();
      if (result.success) {
        const data = result.data || [];
        const processedData = data.map((item) => ({
          ...item,
          createdAtDateOnly: getColombiaDateString(item.createdAt),
          createdAtFormatted: item.createdAt
            ? (() => {
                const colombiaDate = new Date(
                  new Date(item.createdAt).getTime() - 5 * 60 * 60 * 1000
                );
                return colombiaDate.toLocaleDateString("es-CO", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              })()
            : "N/A",
        }));
        const sortedData = [...processedData].sort((a, b) => b.id - a.id);
        setFacturasData(sortedData);
        applyFilters(sortedData, searchQuery, startDate, endDate, statusFilter, typeFilter);
        setTableKey((prev) => prev + 1);
        if (onError) onError(null);
      } else {
        const errorMsg = result.error || "Error al cargar datos";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Error al cargar facturas";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data, search, start, end, status, type) => {
    let filtered = [...data];

    if (search && search.trim()) {
      const query = search.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        return (
          (item.id && item.id.toString().includes(query)) ||
          (item.invoiceNumber && item.invoiceNumber.toLowerCase().includes(query)) ||
          (item.paymentId && item.paymentId.toLowerCase().includes(query)) ||
          (item.nit && item.nit.toLowerCase().includes(query)) ||
          (item.company && item.company.toLowerCase().includes(query)) ||
          (item.codigoEmpresarial && item.codigoEmpresarial.toLowerCase().includes(query)) ||
          (item.status && item.status.toLowerCase().includes(query)) ||
          (item.amount && item.amount.toString().includes(query)) ||
          (item.createdAtFormatted && item.createdAtFormatted.toLowerCase().includes(query))
        );
      });
    }

    if (start || end) {
      filtered = filtered.filter((item) => isDateInRange(item.createdAt, start, end));
    }

    if (status && status !== "todos") {
      filtered = filtered.filter((item) => item.status === status);
    }

    if (type && type !== "todos") {
      filtered = filtered.filter((item) => {
        if (type === "payment") return item.paymentIdType === "payment";
        if (type === "link") return item.paymentIdType === "link";
        if (type === "no_id") return item.paymentIdType === "none";
        return true;
      });
    }

    setFilteredData(filtered);
    setTableKey((prev) => prev + 1);
  };

  useEffect(() => {
    applyFilters(facturasData, searchQuery, startDate, endDate, statusFilter, typeFilter);
  }, [searchQuery, startDate, endDate, statusFilter, typeFilter, facturasData]);

  useEffect(() => {
    fetchFacturas();
  }, [refreshFlag]);

  useEffect(() => {
    const handleRefresh = () => refreshDataPreservePage();
    window.addEventListener("refreshFacturas", handleRefresh);
    return () => window.removeEventListener("refreshFacturas", handleRefresh);
  }, []);

  const clearAllFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("todos");
    setTypeFilter("todos");
  };

  const hasActiveFilters =
    searchQuery ||
    startDate ||
    endDate ||
    (statusFilter && statusFilter !== "todos") ||
    (typeFilter && typeFilter !== "todos");

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      setSnackbar({ open: true, message: "No hay datos para exportar", severity: "warning" });
      return;
    }

    try {
      const exportData = filteredData.map((item) => ({
        ID: item.id,
        "N° Factura": item.invoiceNumber,
        "Código Empresarial": item.codigoEmpresarial,
        "ID Pago": item.paymentId || "N/A",
        "Tipo ID":
          item.paymentIdType === "payment"
            ? "Pago Tarjeta"
            : item.paymentIdType === "link"
            ? "Link/PSE"
            : "N/A",
        NIT: item.nit,
        Empresa: item.company,
        Valor: formatCurrency(item.amount),
        Estado: item.statusBadge?.text || item.status,
        "Fecha Creación": item.createdAtFormatted,
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws["!cols"] = [
        { wch: 8 },
        { wch: 15 },
        { wch: 18 },
        { wch: 25 },
        { wch: 12 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 20 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Facturas");
      XLSX.writeFile(
        wb,
        `facturas_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.xlsx`
      );

      setSnackbar({
        open: true,
        message: `Exportadas ${filteredData.length} facturas`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error al exportar:", error);
      setSnackbar({ open: true, message: "Error al exportar los datos", severity: "error" });
    }
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery("");
  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const handleOpenDeleteModal = (factura) => {
    setFacturaToDelete(factura);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setFacturaToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!facturaToDelete) return;
    setDeleting(true);
    try {
      const result = await deleteInvoiceService(facturaToDelete.id);
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Factura eliminada exitosamente",
          severity: "success",
        });
        await refreshDataPreservePage();
        handleCloseDeleteModal();
      } else {
        setSnackbar({
          open: true,
          message: result.error || "Error al eliminar la factura",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Error al eliminar", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenEditModal = (factura) => {
    setSelectedFactura(factura);
    setModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setModalOpen(false);
    setSelectedFactura(null);
  };

  const handleEditSuccess = async () => {
    await refreshDataPreservePage();
    setSnackbar({ open: true, message: "Factura actualizada exitosamente", severity: "success" });
  };

  const handleOpenPdfModal = (factura) => {
    setSelectedInvoiceForPdf(factura);
    setPdfModalOpen(true);
  };

  const handleClosePdfModal = () => {
    setPdfModalOpen(false);
    setSelectedInvoiceForPdf(null);
  };

  const handlePdfSuccess = (message) => {
    setSnackbar({ open: true, message, severity: "success" });
  };

  // Función para renderizar el ID de pago - CORREGIDA
  const renderPaymentId = ({ value }) => {
    if (!value || value === "No disponible" || value === "N/A") {
      return (
        <ThemedElement component="span" style={{ color: "#999" }}>
          -
        </ThemedElement>
      );
    }

    // Determinar el tipo de ID
    const isPayment = value.startsWith("pay-");
    const isLink = value.startsWith("lnk-");

    // Configurar colores y estilos según el tipo
    const iconColor = isPayment ? colors.success : colors.warning;
    const backgroundColor = isPayment ? alpha(colors.success, 0.1) : alpha(colors.warning, 0.1);
    const label = isPayment ? "Pago con tarjeta" : "Link / PSE";

    // Mostrar el ID completo en el tooltip
    const truncatedDisplay =
      value.length > 25
        ? `${value.substring(0, 20)}...${value.substring(value.length - 5)}`
        : value;

    const handleCopy = async (e) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        setSnackbar({ open: true, message: "ID copiado al portapapeles", severity: "success" });
      } catch (err) {
        setSnackbar({ open: true, message: "Error al copiar", severity: "error" });
      }
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title={`${label}: ${value}`} placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontFamily: "monospace",
              fontSize: "0.75rem",
              backgroundColor: backgroundColor,
              padding: "2px 6px",
              borderRadius: "12px",
              width: "fit-content",
            }}
          >
            {isPayment ? (
              <CreditCard sx={{ fontSize: 14, color: iconColor }} />
            ) : (
              <LinkIcon sx={{ fontSize: 14, color: iconColor }} />
            )}
            <ThemedElement component="span" style={{ color: iconColor, fontWeight: 500 }}>
              {truncatedDisplay}
            </ThemedElement>
          </Box>
        </Tooltip>
        <Tooltip title={`Copiar ${label}`} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              padding: "4px",
              backgroundColor: alpha(iconColor, 0.08),
              "&:hover": { backgroundColor: alpha(iconColor, 0.15) },
            }}
          >
            <ContentCopy sx={{ fontSize: 14, color: iconColor }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderStatusBadge = ({ value }) => {
    const getBackgroundColor = () => {
      if (value?.color === "success") return "#e8f5e9";
      if (value?.color === "warning") return "#fff3e0";
      return "#e3f2fd";
    };
    const getTextColor = () => {
      if (value?.color === "success") return "#2e7d32";
      if (value?.color === "warning") return "#ed6c02";
      return "#0288d1";
    };
    return (
      <ThemedElement
        component="span"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "4px 8px",
          borderRadius: "16px",
          fontSize: "0.75rem",
          fontWeight: "medium",
          backgroundColor: getBackgroundColor(),
          color: getTextColor(),
        }}
      >
        {value?.text === "Aprobado" ? (
          <CheckCircle sx={{ fontSize: 15 }} />
        ) : value?.text === "Pendiente" ? (
          <Schedule sx={{ fontSize: 15 }} />
        ) : (
          <InfoOutlined sx={{ fontSize: 15 }} />
        )}
        <span>{value?.text || "Desconocido"}</span>
      </ThemedElement>
    );
  };

  const renderAmount = ({ value }) => (
    <MDTypography
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: "text.primary",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {formatCurrency(value)}
    </MDTypography>
  );

  const renderAcciones = ({ row }) => {
    const facturaData = row.original?.original || row.original;
    const statusValue = facturaData?.status;
    const isPendiente = statusValue === "pendiente";
    const isAprobado = statusValue === "aprobado";

    return (
      <MDBox display="flex" gap={0.75} justifyContent="center" flexWrap="nowrap">
        {isAprobado && (
          <Tooltip title="Ver comprobante de pago">
            <IconButton
              size="small"
              aria-label="Ver comprobante de pago"
              onClick={() => handleOpenPdfModal(facturaData)}
              sx={{
                ...facturaActionSx,
                color: colors.error,
                bgcolor: "rgba(211,47,47,0.06)",
                "&:hover": { backgroundColor: "#ffebee" },
              }}
            >
              <PictureAsPdf fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip
          title={isPendiente ? "Editar factura" : "Solo se pueden editar facturas pendientes"}
        >
          <span>
            <IconButton
              size="small"
              aria-label="Editar factura"
              onClick={() => isPendiente && handleOpenEditModal(facturaData)}
              disabled={!isPendiente}
              sx={{
                ...facturaActionSx,
                color: isPendiente ? colors.secondary : "#bdbdbd",
                "&:hover": { backgroundColor: isPendiente ? "#e8f5e9" : "transparent" },
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={isPendiente ? "Eliminar factura" : "Solo se pueden eliminar facturas pendientes"}
        >
          <span>
            <IconButton
              size="small"
              aria-label="Eliminar factura"
              onClick={() => isPendiente && handleOpenDeleteModal(facturaData)}
              disabled={!isPendiente}
              sx={{
                ...facturaActionSx,
                color: isPendiente ? colors.error : "#bdbdbd",
                "&:hover": { backgroundColor: isPendiente ? "#ffebee" : "transparent" },
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </MDBox>
    );
  };

  const columns = [
    { Header: "ID", accessor: "id", width: "5%" },
    { Header: "N° Factura", accessor: "invoiceNumber", width: "10%" },
    { Header: "Código Empresarial", accessor: "codigoEmpresarial", width: "10%" },
    { Header: "ID Pago", accessor: "paymentId", width: "14%", Cell: renderPaymentId },
    { Header: "NIT", accessor: "nit", width: "8%" },
    { Header: "Empresa", accessor: "company", width: "12%" },
    { Header: "Valor", accessor: "amount", width: "10%", Cell: renderAmount, align: "right" },
    { Header: "Estado", accessor: "statusBadge", width: "8%", Cell: renderStatusBadge },
    { Header: "Fecha Creación", accessor: "createdAtFormatted", width: "12%" },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "8%",
      Cell: renderAcciones,
      disableSortBy: true,
    },
  ];

  const rows = filteredData.map((item) => ({
    id: item.id,
    invoiceNumber: item.invoiceNumber,
    codigoEmpresarial: item.codigoEmpresarial,
    paymentId: item.paymentId || "No disponible",
    nit: item.nit,
    company: item.company,
    amount: item.amount,
    statusBadge: item.statusBadge,
    createdAtFormatted: item.createdAtFormatted,
    original: item,
  }));

  const stats = {
    total: filteredData.length,
    totalAmount: filteredData.reduce((sum, item) => sum + (Number(item.amount) || 0), 0),
    aprobadosCount: filteredData.filter((item) => item.status === "aprobado").length,
    pendientesCount: filteredData.filter((item) => item.status === "pendiente").length,
    paymentsCount: filteredData.filter((item) => item.paymentIdType === "payment").length,
    linksCount: filteredData.filter((item) => item.paymentIdType === "link").length,
  };

  return (
    <MDBox>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <PaymentReceiptModal
        open={pdfModalOpen}
        onClose={handleClosePdfModal}
        invoiceData={selectedInvoiceForPdf}
        onSuccess={handlePdfSuccess}
      />

      <Card
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <MDTypography variant="h6" sx={{ color: "text.primary", fontWeight: 700 }}>
              Facturas registradas
            </MDTypography>
            <MDTypography sx={{ fontSize: 13, color: "text.secondary" }}>
              {filteredData.length.toLocaleString("es-CO")} facturas en la consulta
            </MDTypography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <MDButton
              variant="outlined"
              onClick={exportToExcel}
              disabled={!filteredData.length}
              startIcon={<Download />}
              sx={{
                textTransform: "none",
                color: colors.secondary,
                borderColor: "#cbd5e1",
                borderRadius: 2,
              }}
            >
              Exportar Excel
            </MDButton>
            <MDButton
              variant="contained"
              onClick={refreshDataPreservePage}
              disabled={loading}
              startIcon={<Refresh />}
              sx={{
                textTransform: "none",
                bgcolor: colors.primary,
                color: "#fff",
                borderRadius: 2,
                "&:hover": { bgcolor: colors.secondary },
              }}
            >
              {loading ? "Cargando..." : "Actualizar"}
            </MDButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
            gap: 1.5,
            px: 2.5,
            py: 2,
            bgcolor: "background.default",
          }}
        >
          {[
            [
              "Facturas",
              stats.total.toLocaleString("es-CO"),
              "#1d4ed8",
              "rgba(37,99,235,0.06)",
              <Receipt key="receipt" />,
            ],
            [
              "Valor total",
              formatCurrency(stats.totalAmount),
              "#166534",
              "rgba(22,101,52,0.06)",
              <CreditCard key="amount" />,
            ],
            [
              "Aprobadas",
              stats.aprobadosCount.toLocaleString("es-CO"),
              "#0f766e",
              "rgba(15,118,110,0.06)",
              <CheckCircle key="approved" />,
            ],
            [
              "Pendientes",
              stats.pendientesCount.toLocaleString("es-CO"),
              "#92400e",
              "rgba(146,64,14,0.06)",
              <Schedule key="pending" />,
            ],
          ].map(([label, value, color, tint, icon]) => (
            <Box
              key={label}
              sx={{
                border: "1px solid",
                borderColor: color,
                bgcolor: tint,
                borderRadius: 2,
                px: 2,
                py: 1.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  alignItems: "center",
                  color,
                  mb: 0.75,
                  "& svg": { fontSize: 17 },
                }}
              >
                {icon}
                <MDTypography sx={{ fontSize: 12, fontWeight: 600, color: "text.primary" }}>
                  {label}
                </MDTypography>
              </Box>
              <MDTypography
                sx={{
                  fontSize: 19,
                  fontWeight: 700,
                  color: "text.primary",
                  overflowWrap: "anywhere",
                }}
              >
                {value}
              </MDTypography>
            </Box>
          ))}
        </Box>

        <MDBox p={2.5}>
          <MDBox display="flex" alignItems="center" gap={1} mb={2.5}>
            <FilterAlt sx={{ color: colors.primary, fontSize: 22 }} />
            <MDTypography
              variant="h6"
              sx={{ color: "text.primary", fontSize: 15 }}
              fontWeight="medium"
            >
              Filtros de Búsqueda
            </MDTypography>
            {hasActiveFilters && (
              <Chip
                label="Filtros activos"
                size="small"
                color="primary"
                onDelete={clearAllFilters}
                sx={{ ml: 1, fontWeight: 600, fontSize: "0.75rem", height: "26px" }}
              />
            )}
          </MDBox>

          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <MDTypography
                variant="caption"
                fontWeight="500"
                mb={0.5}
                sx={{ color: "text.secondary" }}
              >
                BÚSQUEDA GENERAL
              </MDTypography>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid",
                  borderColor: searchQuery ? colors.primary : colors.border,
                  transition: "all 0.2s ease",
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Buscar por ID, factura, ID pago, código, NIT, empresa..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: colors.primary, fontSize: 22 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": { height: "48px" },
                    "& .MuiOutlinedInput-root fieldset": { border: "none" },
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={2}>
              <MDTypography
                variant="caption"
                fontWeight="500"
                mb={0.5}
                sx={{ color: "text.secondary" }}
              >
                FECHA DESDE
              </MDTypography>
              <TextField
                fullWidth
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", height: "48px" } }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <MDTypography
                variant="caption"
                fontWeight="500"
                mb={0.5}
                sx={{ color: "text.secondary" }}
              >
                FECHA HASTA
              </MDTypography>
              <TextField
                fullWidth
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", height: "48px" } }}
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <MDTypography
                variant="caption"
                fontWeight="500"
                mb={0.5}
                sx={{ color: "text.secondary" }}
              >
                ESTADO
              </MDTypography>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ borderRadius: "10px", height: "48px", backgroundColor: colors.white }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendiente">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule sx={{ color: colors.warning }} /> Pendiente
                    </Box>
                  </MenuItem>
                  <MenuItem value="aprobado">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ color: colors.success }} /> Aprobado
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.5}>
              <MDTypography
                variant="caption"
                fontWeight="500"
                mb={0.5}
                sx={{ color: "text.secondary" }}
              >
                TIPO ID PAGO
              </MDTypography>
              <FormControl fullWidth size="small">
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  sx={{ borderRadius: "10px", height: "48px", backgroundColor: colors.white }}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="payment">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CreditCard sx={{ color: colors.success }} /> Pago Tarjeta (pay-*)
                    </Box>
                  </MenuItem>
                  <MenuItem value="link">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinkIcon sx={{ color: colors.warning }} /> Link/PSE (lnk-*)
                    </Box>
                  </MenuItem>
                  <MenuItem value="no_id">
                    <Box display="flex" alignItems="center" gap={1}>
                      Sin ID de pago
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <MDButton
                variant="outlined"
                onClick={clearAllFilters}
                startIcon={<Clear />}
                fullWidth
                disabled={!hasActiveFilters}
                sx={{
                  height: "48px",
                  borderRadius: "10px",
                  borderColor: colors.border,
                  color: colors.darkGray,
                  "&:hover": {
                    borderColor: colors.error,
                    color: colors.error,
                    backgroundColor: "#fef2f2",
                  },
                }}
              >
                Limpiar filtros
              </MDButton>
            </Grid>
            <Grid item xs={12} md={9}>
              {hasActiveFilters && (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <MDTypography variant="caption" fontWeight="500" sx={{ color: "text.secondary" }}>
                    Filtros aplicados:
                  </MDTypography>
                  {searchQuery && (
                    <Chip
                      label={`Búsqueda: "${searchQuery}"`}
                      size="small"
                      onDelete={() => setSearchQuery("")}
                      sx={{
                        height: "28px",
                        backgroundColor: alpha(colors.primary, 0.1),
                        color: colors.primary,
                      }}
                    />
                  )}
                  {startDate && (
                    <Chip
                      label={`Desde: ${formatDateForDisplay(startDate)}`}
                      size="small"
                      onDelete={() => setStartDate("")}
                      sx={{
                        height: "28px",
                        backgroundColor: alpha(colors.primary, 0.1),
                        color: colors.primary,
                      }}
                    />
                  )}
                  {endDate && (
                    <Chip
                      label={`Hasta: ${formatDateForDisplay(endDate)}`}
                      size="small"
                      onDelete={() => setEndDate("")}
                      sx={{
                        height: "28px",
                        backgroundColor: alpha(colors.primary, 0.1),
                        color: colors.primary,
                      }}
                    />
                  )}
                  {statusFilter && statusFilter !== "todos" && (
                    <Chip
                      icon={
                        statusFilter === "pendiente" ? (
                          <Schedule sx={{ fontSize: 14 }} />
                        ) : (
                          <CheckCircle sx={{ fontSize: 14 }} />
                        )
                      }
                      label={statusFilter === "pendiente" ? "Pendiente" : "Aprobado"}
                      size="small"
                      onDelete={() => setStatusFilter("todos")}
                      color={statusFilter === "pendiente" ? "warning" : "success"}
                      sx={{ height: "28px" }}
                    />
                  )}
                  {typeFilter && typeFilter !== "todos" && (
                    <Chip
                      icon={
                        typeFilter === "payment" ? (
                          <CreditCard sx={{ fontSize: 14 }} />
                        ) : typeFilter === "link" ? (
                          <LinkIcon sx={{ fontSize: 14 }} />
                        ) : null
                      }
                      label={
                        typeFilter === "payment"
                          ? "Pago Tarjeta"
                          : typeFilter === "link"
                          ? "Link/PSE"
                          : "Sin ID"
                      }
                      size="small"
                      onDelete={() => setTypeFilter("todos")}
                      sx={{ height: "28px" }}
                    />
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress sx={{ color: colors.primary }} />
        </MDBox>
      ) : error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: "6px" }}
          action={
            <MDButton color="inherit" size="small" onClick={fetchFacturas}>
              Reintentar
            </MDButton>
          }
        >
          {error}
        </Alert>
      ) : filteredData.length === 0 ? (
        <MDBox p={3} textAlign="center">
          <Receipt sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
          <MDTypography variant="h6" sx={{ color: "#64748b" }}>
            {hasActiveFilters
              ? "No se encontraron resultados con los filtros aplicados"
              : "No hay facturas registradas"}
          </MDTypography>
          {hasActiveFilters && (
            <MDButton variant="outlined" onClick={clearAllFilters} sx={{ mt: 2 }}>
              Limpiar filtros
            </MDButton>
          )}
        </MDBox>
      ) : (
        <Box sx={facturaTableSx}>
          <DataTable
            key={tableKey}
            table={{ columns, rows }}
            isSorted={true}
            entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
            showTotalEntries={true}
            noEndBorder
            canSearch={false}
          />
        </Box>
      )}

      <ModalEditarFactura
        open={modalOpen}
        onClose={handleCloseEditModal}
        factura={selectedFactura}
        onSuccess={handleEditSuccess}
      />
      <ModalConfirmarEliminar
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        factura={facturaToDelete}
        loading={deleting}
      />
    </MDBox>
  );
}

TableFacturasContent.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
};

TableFacturasContent.defaultProps = {
  refreshFlag: false,
  onError: null,
};

export default TableFacturasContent;
